import { useMemo } from 'react'
import type { DocumentScanStatus } from '../../../types/documentScanning'
import type { Case } from '../../../hooks/useCase'
import type { WorkflowStage } from '../types'

export interface UseWorkflowStageResult {
  stage: WorkflowStage
  scanStatus: DocumentScanStatus
  scanError: string | null
  statusMessage: string
}

export function useWorkflowStage(caseData: Case | null): UseWorkflowStageResult {
  const scanStatus: DocumentScanStatus = caseData?.documentScan?.status || 'idle'
  const scanError = caseData?.documentScan?.error || null

  // Get status message - prefer humanized message from Firestore, fallback to derived
  const statusMessage = useMemo(() => {
    // Use statusMessage from webhook if available (humanized)
    const firestoreMessage = caseData?.documentScan?.statusMessage
    if (firestoreMessage) return firestoreMessage

    // Fallback to derived message
    switch (scanStatus) {
      case 'scanning': return 'Scanning and classifying documents...'
      case 'awaiting_approval': return 'Scan complete! Review and approve classifications.'
      case 'approved': return 'Classifications approved.'
      case 'failed': return scanError || 'Scan failed'
      default: return ''
    }
  }, [caseData?.documentScan?.statusMessage, scanStatus, scanError])

  // Derive workflow stage from Firestore state
  const stage = useMemo<WorkflowStage>(() => {
    if (scanStatus === 'approved') {
      // Check if there are new documents uploaded after approval (no document_type = not scanned)
      const hasNewDocs = caseData?.documents?.some(doc => !doc.document_type) || false
      return hasNewDocs ? 'approved_with_new' : 'approved'
    }
    if (scanStatus === 'awaiting_approval') return 'review'
    if (scanStatus === 'scanning') return 'scanning'
    // If we have documents but haven't started scanning, show scan button
    if ((caseData?.documents?.length || 0) > 0) return 'scan'
    return 'upload'
  }, [scanStatus, caseData?.documents])

  return {
    stage,
    scanStatus,
    scanError,
    statusMessage,
  }
}

